import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Group, Paper, Stack, Text } from '@mantine/core';
import { IconCloudUpload } from '@tabler/icons-react';
import { FilenameParser } from '../FilenameParser';
import { useUploadStore } from '../uploadStore';
import { IngestcfgSelector } from './IngestcfgSelector';
import { UploadTagsSection } from './UploadTagsSection';
import { VIDEO_EXTENSIONS } from '@/lib/constants/upload';
import type { UploadItem } from '@/lib/types/Upload';
import type { IngestcfgData } from '@/lib/types/Ingest';

interface UploadDropzoneProps {
  addFilesWithMeta: (filePairs: Array<{ file: File; uploadItemId: string }>) => void;
  ingestcfgs: IngestcfgData[];
  selectedIngestcfgId: number | null;
  onSelectIngestcfg: (id: number) => void;
  hasMultiple: boolean;
  isRestricted: boolean;
  cId: number;
  hasFiles: boolean;
}

const ACCEPT = VIDEO_EXTENSIONS.join(',');

function isValidVideoFile(filename: string): boolean {
  const ext = filename.lastIndexOf('.') >= 0
    ? filename.slice(filename.lastIndexOf('.')).toLowerCase()
    : '';
  return (VIDEO_EXTENSIONS as readonly string[]).includes(ext);
}

export function UploadDropzone({
  addFilesWithMeta,
  ingestcfgs,
  selectedIngestcfgId,
  onSelectIngestcfg,
  hasMultiple,
  isRestricted,
  cId,
  hasFiles,
}: UploadDropzoneProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const addItem = useUploadStore((s) => s.addItem);

  // Derive selected profile and tag permissions
  const selectedProfile = useMemo(
    () => ingestcfgs.find((c) => c.ingestcfgId === selectedIngestcfgId) ?? null,
    [ingestcfgs, selectedIngestcfgId],
  );
  const defaultTags = selectedProfile?.tags ?? [];
  const canAddTags = selectedProfile?.allowAddTags ?? false;
  const canEditDefaultTags = selectedProfile?.allowEditDefaultTags ?? false;
  const showTagsSection = defaultTags.length > 0 || canAddTags || canEditDefaultTags;

  // Track effective tags for the current upload session
  const [effectiveTags, setEffectiveTags] = useState<string[]>([]);

  // Reset effective tags when profile selection changes
  useEffect(() => {
    setEffectiveTags(selectedProfile?.tags ?? []);
  }, [selectedIngestcfgId, selectedProfile?.tags]);

  const processFiles = useCallback(
    (files: File[]) => {
      const validFiles = files.filter((f) => isValidVideoFile(f.name));
      if (validFiles.length === 0) return;

      const filePairs: Array<{ file: File; uploadItemId: string }> = [];

      for (const file of validFiles) {
        const parsed = FilenameParser.parseMetadata(file.name, file.lastModified);
        const id = crypto.randomUUID();

        const item: UploadItem = {
          id,
          uppyFileId: '',
          cId,
          filename: file.name,
          fileSize: file.size,
          lastModified: file.lastModified || null,
          title: parsed.title,
          tags: [...effectiveTags],
          ingestcfgId: selectedIngestcfgId ?? 0,
          mediaId: 0,
          ingestJobId: 0,
          s3Key: '',
          status: 'pending',
          uploadProgress: 0,
          processingProgress: 0,
          bytesUploaded: 0,
          bytesTotal: file.size,
          error: null,
          startedAt: null,
          completedAt: null,
          lastProgressAt: null,
          ingestingNotified: false,
        };

        addItem(item);
        filePairs.push({ file, uploadItemId: id });
      }

      addFilesWithMeta(filePairs);
    },
    [addItem, addFilesWithMeta, cId, selectedIngestcfgId, effectiveTags]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragOver(false);
      const files = Array.from(e.dataTransfer.files);
      processFiles(files);
    },
    [processFiles]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  }, []);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files ? Array.from(e.target.files) : [];
      processFiles(files);
      // Reset input so the same file can be re-added after cancel
      if (fileInputRef.current) fileInputRef.current.value = '';
    },
    [processFiles]
  );

  return (
    <Stack gap="sm">
      <IngestcfgSelector
        ingestcfgs={ingestcfgs}
        selectedIngestcfgId={selectedIngestcfgId}
        onSelect={onSelectIngestcfg}
        hasMultiple={hasMultiple}
        isRestricted={isRestricted}
      />

      {showTagsSection && (
        <UploadTagsSection
          defaultTags={defaultTags}
          effectiveTags={effectiveTags}
          onTagsChange={setEffectiveTags}
          canAddTags={canAddTags}
          canEditDefaultTags={canEditDefaultTags}
        />
      )}

      <Paper
        p="md"
        withBorder
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragEnter={handleDragOver}
        onDragLeave={handleDragLeave}
        style={{
          borderStyle: 'dashed',
          borderWidth: 2,
          borderColor: dragOver
            ? 'var(--mantine-color-blue-5)'
            : 'var(--mantine-color-gray-3)',
          backgroundColor: dragOver
            ? 'var(--mantine-color-blue-0)'
            : undefined,
          cursor: 'pointer',
          transition: 'border-color 150ms, background-color 150ms',
          ...(hasFiles ? {} : { flex: 1, minHeight: 280 }),
        }}
        onClick={() => fileInputRef.current?.click()}
      >
        <Stack align="center" justify="center" gap="xs" style={{ height: '100%' }}>
          <IconCloudUpload
            size={hasFiles ? 28 : 48}
            color={dragOver ? 'var(--mantine-color-blue-5)' : 'var(--mantine-color-gray-5)'}
          />
          <Text size={hasFiles ? 'xs' : 'sm'} fw={500} c="dimmed">
            Drag and drop video files here
          </Text>
          <Button size="xs" variant="light" onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}>
            Browse Files
          </Button>
          {!hasFiles && (
            <Text size="xs" c="dimmed">
              {VIDEO_EXTENSIONS.join(', ')}
            </Text>
          )}
        </Stack>
      </Paper>

      <Box display="none">
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={ACCEPT}
          onChange={handleInputChange}
        />
      </Box>
    </Stack>
  );
}
